import React from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { ReactComponent as CalendarSVG } from "go-core/images/svg/calendar.svg";
import { ReactComponent as AvatarSVG } from "../../../../../../images/svg/organization/avatar.svg";
import { ReactComponent as TagSVG } from "../../../../../../images/svg/organization/tag.svg";
import {
	OrganizationPartnerInfoApi,
	OrganizationPartnerInfoProvisionApi,
} from "../../../../../../services/Api/Admin/adminTypes";
import { DrawSubscriptionDetails } from "../services/DrawSubscriptionDetails";
import { getProvisionAmount } from "../services/getProvisionAmount";

interface Props {
	partner: OrganizationPartnerInfoApi;
	size: "large" | "small";
}

const OrganizationSinglePartnerItem = ({ partner, size }: Props) => {
	const { t } = useTranslation("admin");
	const provisions = partner.provisions;

	const drawDeadline = (provision: OrganizationPartnerInfoProvisionApi) => {
		let result = "";

		if (provision.billing_started_at && provision.billing_finished_at)
			result = `${
				provision.billing_started_at[1] < 10
					? `0${provision.billing_started_at[1]}`
					: provision.billing_started_at[1]
			}.${provision.billing_started_at[0]} - ${
				provision.billing_finished_at[1] > 10
					? `0${provision.billing_finished_at[1]}`
					: provision.billing_finished_at[1]
			}.${provision.billing_finished_at[0]}`;
		if (!provision.billing_started_at && !provision.billing_finished_at) result = "~";
		if (!provision.billing_finished_at && provision.billing_started_at)
			result = `${
				provision.billing_started_at[1] < 10
					? `0${provision.billing_started_at[1]}`
					: provision.billing_started_at[1]
			}.${provision.billing_started_at[0]} - ~`;
		if (!provision.billing_started_at && provision.billing_finished_at)
			result = `~ - ${
				provision.billing_finished_at[1] < 10
					? `0${provision.billing_finished_at[1]}`
					: provision.billing_finished_at[1]
			}.${provision.billing_finished_at[0]}`;

		if (provision.billing_period) result += ` (${provision.billing_period})`;
		return result;
	};

	const drawTags = (provision: OrganizationPartnerInfoProvisionApi) =>
		(provision.source || provision.billing_period_type) && (
			<div className="d-flex align-items-center">
				<TagSVG />
				<small className="ms-1 font-weight-bold text-muted">{DrawSubscriptionDetails(provision)}</small>
			</div>
		);

	const isSmall = () => size === "small";
	const isLarge = () => !isSmall();

	const drawContent = () => (
		<>
			<div className="d-flex mb-2">
				<AvatarSVG />
				<div
					className={`d-flex flex-column ms-2 ${
						!partner.partner.type && !partner.is_override && "justify-content-center"
					}`}
				>
					<Link to={`/partnership/${partner.partner_id}`} className="partner-name">
						{partner.partner.name}
					</Link>
					<div className="d-flex align-items-center">
						{partner.partner.type && (
							<small className="text-muted font-weight-bold me-2">
								{t(`enums.partner_types.${partner.partner.type}`)}
							</small>
						)}
						{partner.is_override && (
							<span className="badge bg-success">{t("modules.organization.field.override.title")}</span>
						)}
					</div>
				</div>
			</div>
			<div className="single-partner-card-content">
				{isLarge() && (
					<div className="single-partner-card-content-row single-partner-card-content-row--headlines">
						<div className="name">{t("common.word.subscription")}</div>
						<div className="provision">{t("common.word.provision")}</div>
						<div className="deadline">{t("modules.organization.field.deadline.title")}</div>
						<div className="status">{t("lib:common.word.status")}</div>
					</div>
				)}
				{provisions.map((provision) => (
					<>
						<div className={`single-partner-card-content-row ${isSmall() && "is-small"}`}>
							<div className="name">
								<span className="text-primary">
									{!provision.reference_id
										? t("modules.organization.field.entire_subscription.title")
										: provision.reference_id}
								</span>
								{isLarge() && drawTags(provision)}
							</div>
							<div className="provision">{getProvisionAmount(provision)}</div>
							<div className="deadline">
								{drawDeadline(provision) !== "" && (
									<span>
										<CalendarSVG />
										<small className="ms-1 text-muted font-weight-bold">
											{drawDeadline(provision)}
										</small>
									</span>
								)}
							</div>
							<div className="status">
								<span className={`badge ${provision.finished ? "bg-primary" : "bg-success"}`}>
									{provision.finished
										? t("modules.organization.field.settled.title")
										: t("enums.common.status.ACTIVE")}
								</span>
							</div>
						</div>
						{isSmall() && <div className="small-tags">{drawTags(provision)}</div>}
					</>
				))}
			</div>
			<div className="d-flex flex-column">
				<h6>{t("modules.organization.field.provision_amount.title")}</h6>
				<div className="d-flex align-items-center justify-content-between">
					<small className="text-muted font-weight-bold mb-2">
						{t("modules.organization.field.from_the_beginning.title")}
					</small>
					<span>{FormatMoney(partner.total_provision)}</span>
				</div>
				<div className="d-flex align-items-center justify-content-between">
					<small className="text-muted font-weight-bold">
						{t("modules.organization.field.in_last_month.title")}
					</small>
					<span>{FormatMoney(partner.last_month_provision)}</span>
				</div>
			</div>
		</>
	);

	return isSmall() ? (
		<div>{drawContent()}</div>
	) : (
		<Card className="single-partner-card mb-3">
			<Card.Body>{drawContent()}</Card.Body>
		</Card>
	);
};

export default OrganizationSinglePartnerItem;
